/**
 * 物理トポロジの判定と経路探索。
 *
 * ここでの結論はすべて NetworkWorld の状態から計算する。
 * 「ケーブルを抜いたら ping が落ちる」はこのファイルの isLinkUp が根拠になる。
 */

import type { Device, DeviceId, LinkId, NetworkWorld, Port } from '../types/network';

/** リンクが落ちている理由。UI の演出（ランプの色や説明文）に使う */
export type LinkDownReason =
  | null
  | 'missing-link'
  | 'unplugged-a'
  | 'unplugged-b'
  | 'damaged'
  | 'device-missing'
  | 'device-powered-off'
  | 'port-admin-down'
  | 'port-mismatch';

export interface LinkStatus {
  linkId: LinkId;
  up: boolean;
  reason: LinkDownReason;
  /** リンクアップしている場合のネゴシエート速度（両端の遅い方） */
  speedMbps: number;
}

function getPort(world: NetworkWorld, deviceId: DeviceId, portIndex: number): Port | undefined {
  const device = world.devices[deviceId];
  if (!device) return undefined;
  return device.ports.find((p) => p.index === portIndex);
}

/**
 * リンクの状態を理由付きで返す。
 *
 * 条件:
 *  - 両端が物理的に挿さっている (connectedA && connectedB)
 *  - ケーブルが破損していない (!damaged)
 *  - 両端の機器が存在し、電源が入っている
 *  - 両端のポートが adminUp
 *  - 両端のポートがこのリンクを指している（差し替え途中の不整合を弾く）
 */
export function getLinkStatus(world: NetworkWorld, linkId: LinkId): LinkStatus {
  const link = world.links[linkId];
  if (!link) {
    return { linkId, up: false, reason: 'missing-link', speedMbps: 0 };
  }
  const fail = (reason: LinkDownReason): LinkStatus => ({ linkId, up: false, reason, speedMbps: 0 });

  if (link.damaged) return fail('damaged');
  if (!link.connectedA) return fail('unplugged-a');
  if (!link.connectedB) return fail('unplugged-b');

  const deviceA = world.devices[link.a.deviceId];
  const deviceB = world.devices[link.b.deviceId];
  if (!deviceA || !deviceB) return fail('device-missing');
  if (!deviceA.powered || !deviceB.powered) return fail('device-powered-off');

  const portA = getPort(world, link.a.deviceId, link.a.portIndex);
  const portB = getPort(world, link.b.deviceId, link.b.portIndex);
  if (!portA || !portB) return fail('port-mismatch');
  if (portA.linkId !== linkId || portB.linkId !== linkId) return fail('port-mismatch');
  if (!portA.adminUp || !portB.adminUp) return fail('port-admin-down');

  // ポートに書かれた speedMbps が 0 のときは 100Mbps を既定とする
  // （0 は「未リンク」を意味する値なので、リンクアップ時の速度としては使わない）
  const speeds = [portA.speedMbps, portB.speedMbps].filter((s) => s > 0);
  const speedMbps = speeds.length > 0 ? Math.min(...speeds) : 100;
  return { linkId, up: true, reason: null, speedMbps };
}

/** リンクが正常に上がっているか */
export function isLinkUp(world: NetworkWorld, linkId: LinkId): boolean {
  return getLinkStatus(world, linkId).up;
}

/** この機器が通信に参加できる状態か（電源が入っていて、隔離されていない） */
export function isDeviceOperational(device: Device | undefined): device is Device {
  return !!device && device.powered && !device.quarantined;
}

/** 機器につながっている「上がっているリンク」の一覧 */
export function upLinksOf(world: NetworkWorld, deviceId: DeviceId): LinkId[] {
  const result: LinkId[] = [];
  for (const linkId of Object.keys(world.links)) {
    const link = world.links[linkId];
    if (link.a.deviceId !== deviceId && link.b.deviceId !== deviceId) continue;
    if (isLinkUp(world, linkId)) result.push(linkId);
  }
  return result;
}

/** リンクの反対側の機器 ID */
export function otherEnd(world: NetworkWorld, linkId: LinkId, deviceId: DeviceId): DeviceId | null {
  const link = world.links[linkId];
  if (!link) return null;
  if (link.a.deviceId === deviceId) return link.b.deviceId;
  if (link.b.deviceId === deviceId) return link.a.deviceId;
  return null;
}

export interface PathOptions {
  /** リンクの up/down を無視して配線図どおりに探索する（切断か未配線かの切り分け用） */
  ignoreLinkState?: boolean;
  /** 電源断・隔離を無視する */
  ignoreDeviceState?: boolean;
}

/**
 * 経路探索（BFS）。最短ホップの経路を機器 ID の配列で返す。到達不能なら null。
 *
 * - 電源が落ちている機器・隔離された機器は経由しない（始点／終点でも同様）
 * - リンクが落ちている経路は使わない
 */
export function findPath(
  world: NetworkWorld,
  fromDeviceId: DeviceId,
  toDeviceId: DeviceId,
  options: PathOptions = {},
): DeviceId[] | null {
  const from = world.devices[fromDeviceId];
  const to = world.devices[toDeviceId];
  if (!from || !to) return null;

  const usable = (device: Device | undefined): device is Device =>
    options.ignoreDeviceState ? !!device : isDeviceOperational(device);

  if (!usable(from) || !usable(to)) return null;
  if (fromDeviceId === toDeviceId) return [fromDeviceId];

  const prev = new Map<DeviceId, DeviceId | null>();
  prev.set(fromDeviceId, null);
  const queue: DeviceId[] = [fromDeviceId];

  while (queue.length > 0) {
    const current = queue.shift() as DeviceId;
    if (current === toDeviceId) break;

    for (const linkId of Object.keys(world.links)) {
      const link = world.links[linkId];
      if (link.a.deviceId !== current && link.b.deviceId !== current) continue;
      if (!options.ignoreLinkState && !isLinkUp(world, linkId)) continue;

      const next = otherEnd(world, linkId, current);
      if (next === null || prev.has(next)) continue;
      if (!usable(world.devices[next])) continue;

      prev.set(next, current);
      queue.push(next);
    }
  }

  if (!prev.has(toDeviceId)) return null;

  const path: DeviceId[] = [];
  let cursor: DeviceId | null = toDeviceId;
  while (cursor !== null) {
    path.unshift(cursor);
    cursor = prev.get(cursor) ?? null;
  }
  return path;
}

/** 経路上で隣り合う 2 機器を結ぶリンク ID（複数あれば最初に見つかった up のもの） */
export function linkBetween(
  world: NetworkWorld,
  deviceA: DeviceId,
  deviceB: DeviceId,
  requireUp = true,
): LinkId | null {
  let fallback: LinkId | null = null;
  for (const linkId of Object.keys(world.links)) {
    const link = world.links[linkId];
    const match =
      (link.a.deviceId === deviceA && link.b.deviceId === deviceB) ||
      (link.a.deviceId === deviceB && link.b.deviceId === deviceA);
    if (!match) continue;
    if (isLinkUp(world, linkId)) return linkId;
    fallback = linkId;
  }
  return requireUp ? null : fallback;
}

/** 経路を構成するリンクの一覧 */
export function linksAlongPath(world: NetworkWorld, path: DeviceId[]): LinkId[] {
  const result: LinkId[] = [];
  for (let i = 0; i + 1 < path.length; i++) {
    const linkId = linkBetween(world, path[i], path[i + 1], false);
    if (linkId) result.push(linkId);
  }
  return result;
}

/** IP アドレスから機器を引く（電源が落ちていても引ける。重複時は最初の 1 台） */
export function findDeviceByIp(world: NetworkWorld, ip: string): Device | null {
  for (const id of Object.keys(world.devices)) {
    const device = world.devices[id];
    if (device.ip.address === ip || device.ipInterfaces?.some(iface => iface.address === ip)) return device;
  }
  return null;
}

/** 同じ IP を持つ機器をすべて返す */
export function findDevicesByIp(world: NetworkWorld, ip: string): Device[] {
  return Object.keys(world.devices)
    .map((id) => world.devices[id])
    .filter((device) => device.ip.address === ip || device.ipInterfaces?.some(iface => iface.address === ip));
}

/** 表示名（label）から機器を引く。大文字小文字とホスト名っぽい表記を吸収する */
export function findDeviceByName(world: NetworkWorld, name: string): Device | null {
  const normalized = name.trim().toLowerCase();
  for (const id of Object.keys(world.devices)) {
    const device = world.devices[id];
    if (device.id.toLowerCase() === normalized) return device;
    if (device.label.toLowerCase() === normalized) return device;
  }
  return null;
}
